import { CellContext } from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line, Area, ComposedChart, Tooltip, YAxis } from "recharts";
import dayjs from "dayjs";
import { alignComparisonDataByIndex } from "@lib/util-functions/comparisonBuckets";
import { dashboardModeAtom, historicalChartMetricAtom } from "@/atoms/store";
import { useAtom, useAtomValue } from "jotai";
import { useNewVsReturningState, type NvrType } from "@lib/hooks/url-state";
import type {
  DailyMetric,
  FixedKpiMetric,
  KpiMetric,
} from "@lib/api-hooks/useKpisOverview";
import { ChartConfig, ChartContainer } from "@tracify/ui/chart";
import {
  availableHistoricalChartMetrics,
  type HistoricalChartMetric,
} from "@lib/hooks/constants";
import {
  getMetricLabelForDashboardMode,
  METRIC_DECIMALS,
  METRIC_SUFFIXES,
} from "@/constants/constants";
import { useFormatContext } from "@lib/hooks/format-context";

type Props<T> = {
  compared?: boolean;
  disabledMetrics?: HistoricalChartMetric[];
  info: CellContext<T, number | undefined>;
};

const getHistoricalMetricValue = (
  values: DailyMetric,
  nvrKey: NvrType,
  metric: HistoricalChartMetric
) => {
  if (values.fixed && metric in values.fixed) {
    return values.fixed[metric as keyof FixedKpiMetric] ?? 0;
  }

  return values[nvrKey]?.[metric as keyof KpiMetric] ?? 0;
};

function HistoricalChartDisplay<T extends Record<string, any>>({
  info,
  compared,
  disabledMetrics,
}: Props<T>) {
  const [selectedHistoricalChartMetric] = useAtom(historicalChartMetricAtom);
  const historicalChartMetric = disabledMetrics?.includes(
    selectedHistoricalChartMetric
  )
    ? (availableHistoricalChartMetrics.find(
        (metric) => !disabledMetrics.includes(metric)
      ) ?? selectedHistoricalChartMetric)
    : selectedHistoricalChartMetric;
  const { newVsReturning: nvrKey } = useNewVsReturningState();
  const dashboardMode = useAtomValue(dashboardModeAtom);
  const { calculateCurrencyConvertion, formatNumber } = useFormatContext();
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const hoveredChartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hideTooltip = () => {
      if (!hoveredChartRef.current) return;
      hoveredChartRef.current = null;
      setIsHovered(false);
    };
    const handlePointerMove = (event: MouseEvent) => {
      if (
        hoveredChartRef.current &&
        (!(event.target instanceof Node) ||
          !hoveredChartRef.current.contains(event.target))
      ) {
        hideTooltip();
      }
    };
    const handlePointerOut = (event: MouseEvent) => {
      if (
        !(event.relatedTarget instanceof Node) ||
        !hoveredChartRef.current?.contains(event.relatedTarget)
      ) {
        hideTooltip();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) hideTooltip();
    };

    // Capture outside movement even if a fast exit skips the chart's leave
    // handler. Register before hovering so a quick enter/exit cannot race it.
    window.addEventListener("mousemove", handlePointerMove, true);
    window.addEventListener("pointermove", handlePointerMove, true);
    window.addEventListener("mouseout", handlePointerOut, true);
    window.addEventListener("blur", hideTooltip);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("mousemove", handlePointerMove, true);
      window.removeEventListener("pointermove", handlePointerMove, true);
      window.removeEventListener("mouseout", handlePointerOut, true);
      window.removeEventListener("blur", hideTooltip);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const metricLabel = getMetricLabelForDashboardMode(
    historicalChartMetric,
    dashboardMode
  );
  const formatValue = (value: number) => {
    const decimals = METRIC_DECIMALS[historicalChartMetric] ?? 0;
    const suffix = METRIC_SUFFIXES[historicalChartMetric];
    if (suffix === "€") {
      return calculateCurrencyConvertion(value, decimals);
    }
    if (suffix === "%") {
      return `${formatNumber(value * 100, decimals)}%`;
    }
    return formatNumber(value, decimals);
  };
  const comparedValue = info.row.original?.compared?.historical as
    | DailyMetric[]
    | undefined;
  const value = info.getValue() as DailyMetric[] | undefined;

  const newValues: {
    date: string;
    value: number;
    compared_value?: number;
    compared_date?: string;
  }[] = [];
  let index = 0;

  if (comparedValue && value) {
    // Use index-based alignment for comparison data
    const comparisonMap = alignComparisonDataByIndex(value, comparedValue);

    for (const values of value ?? []) {
      const comparedDateValue = comparisonMap.get(values.date);
      newValues[index] = {
        date: values.date,
        value: getHistoricalMetricValue(values, nvrKey, historicalChartMetric),
      };
      if (comparedDateValue) {
        newValues[index].compared_date = comparedDateValue.date;
        newValues[index].compared_value = getHistoricalMetricValue(
          comparedDateValue,
          nvrKey,
          historicalChartMetric
        );
      }
      index++;
    }
  } else {
    for (const values of value ?? []) {
      newValues[index] = {
        date: values.date,
        value: getHistoricalMetricValue(values, nvrKey, historicalChartMetric),
      };
      index++;
    }
  }

  // Chart config
  const chartConfig = useMemo<ChartConfig>(
    () => ({
      value: {
        label: "Value",
        color: "#6254FF",
      },
      compared_value: {
        label: "Compared Value",
        color: "#6254FF",
      },
    }),
    []
  );

  return (
    <ChartContainer
      config={chartConfig}
      className="h-12 w-full max-w-xs"
      onMouseLeave={() => {
        hoveredChartRef.current = null;
        setIsHovered(false);
      }}
      onMouseMove={(event) => {
        hoveredChartRef.current = event.currentTarget;
        setIsHovered(true);
        setTooltipPosition({
          x: Math.max(8, Math.min(event.clientX + 12, window.innerWidth - 288)),
          y: Math.max(8, event.clientY - (compared ? 120 : 84)),
        });
      }}
    >
      <ComposedChart data={newValues}>
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Tooltip
          active={isHovered ? undefined : false}
          // Render outside the table's scrolling container to avoid clipping.
          portal={typeof document !== "undefined" ? document.body : undefined}
          isAnimationActive={false}
          wrapperStyle={{
            position: "fixed",
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            width: 280,
            pointerEvents: "none",
            zIndex: 100,
          }}
          content={({ active, payload }) => {
            if (!isHovered || !active || !payload?.length) return null;

            return (
              <div
                role="tooltip"
                className="rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground shadow-xl"
              >
                <div className="mb-1 font-medium">
                  {dayjs(payload[0].payload.date).format("DD MMM YYYY")}
                </div>
                {payload.map((entry) => (
                  <div
                    key={String(entry.dataKey)}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-muted-foreground">
                      {entry.dataKey === "compared_value"
                        ? `Previous (${dayjs(
                            entry.payload.compared_date
                          ).format("DD MMM YYYY")})`
                        : metricLabel}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatValue(Number(entry.value))}
                    </span>
                  </div>
                ))}
              </div>
            );
          }}
        />
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-value)"
              stopOpacity={0.6}
            />
            <stop offset="80%" stopColor="var(--color-value)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type="monotone"
          dataKey="value"
          stroke="var(--color-value)"
          fillOpacity={compared ? 0 : 1}
          fill="url(#colorValue)"
          dot={false}
          activeDot={isHovered}
          connectNulls
        />
        {compared ? (
          <Line
            type="monotone"
            dataKey="compared_value"
            strokeDasharray={"5 5"}
            stroke="var(--color-compared_value)"
            strokeWidth={2}
            dot={false}
            activeDot={isHovered}
            connectNulls
          />
        ) : null}
      </ComposedChart>
    </ChartContainer>
  );
}

export default HistoricalChartDisplay;
